import { ListGroup, ListRow, Sheet, cn } from '@ui'
import React, { FC, useMemo, useState } from 'react'
import { HiCheck, HiChevronDown } from 'react-icons/hi'

interface Value {
  key: string
  humanValue: string
}

interface Props {
  heading: string
  values: Value[]
  currentValue: string
  onValueChange: (value: string) => void
}

const Picker: FC<Props> = ({ heading, values, currentValue, onValueChange }) => {
  const [pickerVisible, setPickerVisible] = useState(false)

  const onValueSelect = (value: Value) => {
    onValueChange(value.key)
    setPickerVisible(false)
  }

  const selectedValue = useMemo(() => {
    return values.find((value) => value.key === currentValue)
  }, [currentValue, values])

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={() => setPickerVisible(true)}
        className={cn(
          'ring-mask flex min-h-12 w-full cursor-pointer items-center gap-2 rounded-md border-0 bg-field px-4 py-3 text-left font-text text-[16px]',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
          currentValue
            ? 'text-fg [--ring-fill:var(--plug-field-ring-focus)]'
            : 'text-fg-3 [--ring-fill:var(--plug-field-ring)]'
        )}
      >
        <span className="flex-1 truncate">{selectedValue?.humanValue || heading}</span>
        <HiChevronDown aria-hidden="true" className="size-5 shrink-0 text-fg-3" />
      </button>
      <Sheet open={pickerVisible} onClose={() => setPickerVisible(false)} title={heading} closable>
        <ListGroup className="max-h-[300px] overflow-y-auto">
          {values.map((value) => (
            <ListRow
              key={value.key}
              dense
              primary={value.humanValue}
              selected={value.key === currentValue}
              trailing={
                value.key === currentValue ? (
                  <HiCheck aria-hidden="true" className="size-5 text-accent" />
                ) : undefined
              }
              onClick={() => onValueSelect(value)}
            />
          ))}
        </ListGroup>
      </Sheet>
    </>
  )
}

export default Picker
